/**
 * NotificationRow — single row for the dropdown + full-history views.
 *
 * Two visual modes:
 *   - `compact` (dropdown): 2-line row with body_short + relative
 *     time; click anywhere navigates + marks-read optimistically.
 *   - `full` (history route): 3-line row with full body + Read /
 *     Dismiss / Delete action buttons on the right.
 *
 * Optimistic-navigation rule (per user CP2 brief):
 *   Click → immediately mark-read locally + call the parent's
 *   `onMarkRead` (fire-and-forget), THEN navigate. If the POST
 *   fails silently, the navigation still succeeds and the dropdown
 *   will retry the mark-read on next open (per parent state).
 */
import { Link, useNavigate } from "react-router-dom";
import { Check, X, Trash2 } from "lucide-react";
import { metaFor, formatRelative } from "./utils";

export default function NotificationRow({
  notification,
  mode = "compact", // "compact" | "full"
  onMarkRead,
  onDismiss,
  onDelete,
  onNavigateClose,
}) {
  const meta = metaFor(notification.notification_type);
  const Icon = meta.icon;
  const isUnread = !notification.is_read;
  const navigate = useNavigate();
  const isCompact = mode === "compact";

  function handleRowActivate(e) {
    // Prevent double navigation when the row *is* a Link — we handle
    // it programmatically here so mark-read fires first (still
    // synchronous / non-blocking; the POST is fire-and-forget).
    e.preventDefault();
    if (isUnread) onMarkRead?.(notification);
    if (notification.action_url) {
      navigate(notification.action_url);
      onNavigateClose?.();
    }
  }

  const Wrapper = notification.action_url && isCompact ? Link : "div";
  const wrapperProps = notification.action_url && isCompact
    ? {
        to: notification.action_url,
        onClick: handleRowActivate,
      }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`notification-row-${notification.id}`}
      data-notification-type={notification.notification_type}
      data-unread={isUnread ? "true" : "false"}
      className={`block px-4 py-3 transition-colors ${
        isUnread ? "bg-v2-surfaceSubtle/60 hover:bg-v2-surfaceSubtle" : "hover:bg-v2-surfaceSubtle/50"
      }`}
      style={{
        borderBottom: "1px solid var(--v2-border)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
          style={{
            background: meta.tintBg,
            border: `1px solid ${meta.tintBorder}`,
          }}
        >
          <Icon className="w-4 h-4" style={{ color: meta.tint }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p
              data-testid={`notification-row-title-${notification.id}`}
              className={`text-v2-body-sm leading-tight ${isUnread ? "font-semibold text-v2-text" : "font-medium text-v2-textMuted"} truncate`}
            >
              {notification.title}
            </p>
            {isUnread && (
              <span
                data-testid={`notification-row-unread-dot-${notification.id}`}
                role="status"
                aria-label="Unread"
                className="mt-1.5 inline-block h-2 w-2 rounded-full flex-shrink-0"
                style={{ background: "var(--v2-accent)" }}
              />
            )}
          </div>
          <p
            data-testid={`notification-row-body-${notification.id}`}
            className={`mt-1 text-v2-caption text-v2-textMuted ${isCompact ? "line-clamp-2" : ""} leading-relaxed`}
          >
            {isCompact ? (notification.body_short || notification.body) : notification.body}
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap text-v2-caption text-v2-textSubtle">
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]"
              style={{
                background: meta.tintBg,
                border: `1px solid ${meta.tintBorder}`,
                color: meta.tint,
              }}
            >
              {meta.label}
            </span>
            <span data-testid={`notification-row-time-${notification.id}`}>
              {formatRelative(notification.created_at)}
            </span>
          </div>
        </div>
        {!isCompact && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {isUnread && (
              <button
                type="button"
                onClick={() => onMarkRead?.(notification)}
                data-testid={`notification-row-mark-read-${notification.id}`}
                title="Mark read"
                aria-label="Mark read"
                className="p-1.5 rounded-lg text-v2-textMuted hover:text-v2-text hover:bg-v2-surfaceSubtle transition-colors"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
            )}
            {!notification.is_dismissed && (
              <button
                type="button"
                onClick={() => onDismiss?.(notification)}
                data-testid={`notification-row-dismiss-${notification.id}`}
                title="Dismiss"
                aria-label="Dismiss"
                className="p-1.5 rounded-lg text-v2-textMuted hover:text-v2-text hover:bg-v2-surfaceSubtle transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => onDelete?.(notification)}
              data-testid={`notification-row-delete-${notification.id}`}
              title="Delete"
              aria-label="Delete"
              className="p-1.5 rounded-lg text-red-400/80 hover:text-red-400 hover:bg-red-400/[0.06] transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </Wrapper>
  );
}
